import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";

// Home is the landing page — keep it eager so first paint is instant.
import Home from "@/pages/Home";

// Every other route is code-split.
const Article                 = lazy(() => import("@/pages/Article"));
const Category                = lazy(() => import("@/pages/Category"));
const Search                  = lazy(() => import("@/pages/Search"));
const Login                   = lazy(() => import("@/pages/Login"));
const NewsroomForgotPassword  = lazy(() => import("@/pages/NewsroomForgotPassword"));
const NewsroomResetPassword   = lazy(() => import("@/pages/NewsroomResetPassword"));
const Admin                   = lazy(() => import("@/pages/Admin"));
const TheBar                  = lazy(() => import("@/pages/TheBar"));
const Markets                 = lazy(() => import("@/pages/Markets"));
const Midterms                = lazy(() => import("@/pages/Midterms"));
const InConversationWithPage  = lazy(() => import("@/pages/InConversationWithPage"));
const GuestPage               = lazy(() => import("@/pages/GuestPage"));
const News                    = lazy(() => import("@/pages/News"));
const Sport                   = lazy(() => import("@/pages/Sport"));
const RugbyCompetition        = lazy(() => import("@/pages/RugbyCompetition"));
const CricketCompetition      = lazy(() => import("@/pages/CricketCompetition"));
const HintonShops             = lazy(() => import("@/pages/HintonShops"));
const Archives                = lazy(() => import("@/pages/Archives"));
const About                   = lazy(() => import("@/pages/About"));
const AcceptInvite            = lazy(() => import("@/pages/AcceptInvite"));
const AuthorPage              = lazy(() => import("@/pages/AuthorPage"));
const AdminPreview            = lazy(() => import("@/pages/AdminPreview"));
const WorkingWithHinton       = lazy(() => import("@/pages/WorkingWithHinton"));
const WriteForUs              = lazy(() => import("@/pages/WriteForUs"));
const PrivacyPolicy           = lazy(() => import("@/pages/PrivacyPolicy"));
const CookiePolicy            = lazy(() => import("@/pages/CookiePolicy"));
const LegacyRedirect          = lazy(() => import("@/pages/LegacyRedirect"));

const RouteFallback = () => (
  <div style={{ minHeight: "60vh" }} aria-hidden="true" />
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            <CookieConsent />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Layout><Home /></Layout>} />
                <Route path="/article/:slug" element={<Layout><Article /></Layout>} />
                <Route path="/category/:slug" element={<Layout><Category /></Layout>} />
                <Route path="/search" element={<Layout><Search /></Layout>} />
                <Route path="/hinton-bar" element={<Layout><TheBar /></Layout>} />
                <Route path="/markets" element={<Layout><Markets /></Layout>} />
                <Route path="/midterms" element={<Layout><Midterms /></Layout>} />
                <Route path="/in-conversation-with" element={<Layout><InConversationWithPage /></Layout>} />
                <Route path="/in-conversation-with/:slug" element={<Layout><GuestPage /></Layout>} />
                <Route path="/news" element={<Layout><News /></Layout>} />
                <Route path="/sport" element={<Layout><Sport /></Layout>} />
                <Route path="/sport/rugby/:slug" element={<Layout><RugbyCompetition /></Layout>} />
                <Route path="/sport/cricket/:slug" element={<Layout><CricketCompetition /></Layout>} />
                <Route path="/hinton-shops" element={<Layout><HintonShops /></Layout>} />
                <Route path="/archives" element={<Layout><Archives /></Layout>} />
                <Route path="/working-with-hinton" element={<Layout><WorkingWithHinton /></Layout>} />
                <Route path="/write-for-us" element={<Layout><WriteForUs /></Layout>} />
                <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
                <Route path="/cookies" element={<Layout><CookiePolicy /></Layout>} />
                <Route path="/cookie-policy" element={<Layout><CookiePolicy /></Layout>} />
                <Route path="/login" element={<Login />} />
                <Route path="/login/forgot-password" element={<NewsroomForgotPassword />} />
                <Route path="/login/reset-password" element={<NewsroomResetPassword />} />
                <Route path="/accept-invite/:token" element={<AcceptInvite />} />
                <Route path="/author/:slug" element={<Layout><AuthorPage /></Layout>} />
                <Route path="/admin" element={<Layout><Admin /></Layout>} />
                <Route path="/admin/preview/:id" element={<Layout><AdminPreview /></Layout>} />
                {/* Legacy HintonMagazine.com Wix paths — production fallback for 301 routing. */}
                <Route path="/post/:slug" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/blog" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/blog/categories/:slug" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/lwf-25" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/news-and-business" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/health" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/about-hinton" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/book-stand" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/hm-visits" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/hinton-meets" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/culture" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/fashion" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/art-and-design" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/technology" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/food-drink" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/pop-culture" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/lifestyle" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/about" element={<Layout><About /></Layout>} />
                <Route path="/about-us" element={<Layout><About /></Layout>} />
                <Route path="/contact" element={<Layout><About /></Layout>} />
                <Route path="/contact-us" element={<Layout><About /></Layout>} />
                <Route path="/team" element={<Layout><About /></Layout>} />
                {/* Legacy subscriber-side routes — subscription flow is retired.
                    Send anyone still on these URLs back to the home page. */}
                <Route path="/subscribe" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/signin" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/signup" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/account" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/forgot-password" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/reset-password" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="/accept-membership/:token" element={<Layout><LegacyRedirect /></Layout>} />
                <Route path="*" element={<Layout><LegacyRedirect /></Layout>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
